import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { floorplannerStore } from '../../store/floorplannerStore';


import { SingleLineType, WallType } from '../../types/floorplannerTypes';
import { Vector2 } from 'three';
import { editorStore } from '../../store/editorStore';
import { IoClose } from 'react-icons/io5';
import { observer } from 'mobx-react-lite';
import drawElevation from '../../assets/Draw-elevation ny-01.png'
import drawFloor from '../../assets/Draw-walls-01.png'
import singleLine from '../../assets/symbols/Single line_Rityta 1.png'
import singleWall from '../../assets/symbols/Single wall_Rityta 1.png'
import arcWall from '../../assets/curvedWallIcon.svg';
import { getDefaultControlPoint } from '../../utils/arcUtils';
import { SymbolLibraryTabBar } from './SymbolLibraryTabs';

interface DropdownProps {
  position: { top: number; left: number };
  onClose: () => void;
  dropdownRef: React.Ref<HTMLDivElement>;
}

const CurvedLineIcon = () => (
  <svg
    className="w-[108px] h-[87px] left-[3px] top-[3px] absolute rounded-sm bg-[#f5f5f5]"
    viewBox="0 0 108 87"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M25 58C38 31 70 31 83 58"
      stroke="#303030"
      strokeWidth="4"
      strokeLinecap="round"
      fill="none"
    />
  </svg>
);

type StructureToolId =
  | "Wall"
  | "SingleLine"
  | "CurvedLine"
  | "WallConstructionMode"
  | "CurvedWall"
  | "LineConstructionMode";

type StructureTab = "FLOOR_PLAN" | "ELEVATION";

interface StructureTool {
  id: StructureToolId;
  labelKey: string;
  preview: React.ReactNode;
}

const FLOOR_PLAN_TOOLS: StructureTool[] = [
  {
    id: "WallConstructionMode",
    labelKey: "walls.drawFloorplan",
    preview: (
      <img
        className="w-[108px] h-[87px] left-[3px] top-[3px] absolute rounded-sm"
        src={drawFloor}
        alt=""
      />
    ),
  },
  {
    id: "Wall",
    labelKey: "walls.singleWall",
    preview: (
      <img
        className="w-[108px] h-[87px] left-[3px] top-[3px] absolute rounded-sm"
        src={singleWall}
        alt=""
      />
    ),
  },
  {
    id: "CurvedWall",
    labelKey: "walls.curvedWall",
    preview: (
      <img
        className="w-[108px] h-[87px] left-[3px] top-[3px] absolute rounded-sm"
        src={arcWall}
        alt=""
      />
    ),
  },
];

const ELEVATION_TOOLS: StructureTool[] = [
  {
    id: "LineConstructionMode",
    labelKey: "walls.drawElevation",
    preview: (
      <img
        className="w-[108px] h-[87px] left-[3px] top-[3px] absolute rounded-sm"
        src={drawElevation}
        alt=""
      />
    ),
  },
  {
    id: "SingleLine",
    labelKey: "walls.singleLine",
    preview: (
      <img
        className="w-[108px] h-[87px] left-[3px] top-[3px] absolute rounded-sm"
        src={singleLine}
        alt=""
      />
    ),
  },
  {
    id: "CurvedLine",
    labelKey: "walls.curvedLine",
    preview: <CurvedLineIcon />,
  },
];

export const WallsPanel: React.FC<DropdownProps> = observer(({ position, onClose, dropdownRef }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<StructureTab>("FLOOR_PLAN");

  // Prevent clicks inside the panel from closing it
  const handlePanelClick = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();  // Prevents the event from bubbling up
  };

  const handleIconClick = (label: StructureToolId) => {
    // Another collaborator holds the edit lease, so this view is read-only.
    if (floorplannerStore.lockedByOther) return;
    // Retrieve the visible center from the editor store
    const [centerX, centerY] = editorStore.getVisibleCenter();
    if (label === "Wall") {
      // Set a default wall length, for example 5 units (adjust based on your needs)
      const wallLength = 2;

      // Calculate the start and end points of the wall, centered around the visible center
      const startX = centerX - wallLength / 2;
      const endX = centerX + wallLength / 2;
      const startY = centerY;
      const endY = centerY;

      const wall: WallType = {
        id: floorplannerStore.generateId(),
        type: "wall",
        start: new Vector2(startX, startY), // Start point to the left of the center
        end: new Vector2(endX, endY), // End point to the right of the center

      }
      floorplannerStore.addWall(wall);
      // Select the newly created wall so it appears in the highlight view
      editorStore.clearSelections();
      editorStore.addSelection(wall);
    } else if (label === "SingleLine") {
      // Set a default line length, for example 5 units (adjust based on your needs)
      const singleLineLength = 2;
      // Calculate the start and end points of the wall, centered around the visible center
      const startX = centerX - singleLineLength / 2;
      const endX = centerX + singleLineLength / 2;
      const startY = centerY;
      const endY = centerY;

      const line: SingleLineType = {
        id: floorplannerStore.generateId(),
        type: "singleLine",
        start: new Vector2(startX, startY), // Start point to the left of the center
        end: new Vector2(endX, endY), // End point to the right of the center
      }
      floorplannerStore.addSingleLine(line);
    } else if (label === "CurvedLine") {
      const singleLineLength = 2;
      const startX = centerX - singleLineLength / 2;
      const endX = centerX + singleLineLength / 2;
      const startY = centerY;
      const endY = centerY;

      const start = new Vector2(startX, startY);
      const end = new Vector2(endX, endY);
      const line: SingleLineType = {
        id: floorplannerStore.generateId(),
        type: "singleLine",
        start,
        end,
        lineForm: "arc",
        controlPoint: getDefaultControlPoint(start, end, 0.5),
      }
      floorplannerStore.addSingleLine(line);
      floorplannerStore.selectLine(line.id);
      editorStore.clearSelections();
      editorStore.addSelection(line);
    }  else if (label === "WallConstructionMode") {
      editorStore.setWallConstructionMode(!editorStore.wallConstructionMode);
      onClose();
    } else if (label === "CurvedWall") {
      const wallLength = 2;
      const startX = centerX - wallLength / 2;
      const endX = centerX + wallLength / 2;
      const startY = centerY;
      const endY = centerY;

      const start = new Vector2(startX, startY);
      const end = new Vector2(endX, endY);
      const wall: WallType = {
        id: floorplannerStore.generateId(),
        type: "wall",
        start,
        end,
        lineForm: "arc",
        controlPoint: getDefaultControlPoint(start, end, 0.5),
      }
      floorplannerStore.addWall(wall);
      editorStore.clearSelections();
      editorStore.addSelection(wall);
    } else if (label === "LineConstructionMode") {
      editorStore.setLineConstructionMode(!editorStore.lineConstructionMode);
      onClose();
    }
  };

  const tools = activeTab === "FLOOR_PLAN" ? FLOOR_PLAN_TOOLS : ELEVATION_TOOLS;

  return (
    <div
      className="fixed z-50 w-fit bg-white rounded shadow-lg mb-8 overflow-y-auto scrollbar"
      style={{ top: position.top, left: position.left, maxHeight: '70vh' }}
      ref={dropdownRef}
      onClick={handlePanelClick}
    >
      <div className="flex flex-cols items-center justify-between border-b border-[#edebe6] p-2 px-3 pr-2">
        <div className="text-sm font-normal">{t('panels.structureLibrary')}</div>
        <div onClick={onClose} className="text-sm text-gray-500"><IoClose style={{ strokeWidth: 0.5 }} size={24} /></div>
      </div>

      <div className="px-3 py-3 border-b border-[#edebe6]">
        <SymbolLibraryTabBar
          activeTab={activeTab}
          onTabChange={(tab) => setActiveTab(tab as StructureTab)}
        />
      </div>

      <div className={`mb-4 p-2 px-3${floorplannerStore.lockedByOther ? ' opacity-50' : ''}`}>
        {activeTab === "ELEVATION" && (
          <div className="mb-2 text-xs font-normal text-gray-500">
            {t('structures.elevationUnavailableIn3D')}
          </div>
        )}
        <div className="w-fit">
          <div className="grid grid-cols-[114px_114px] gap-x-0 gap-y-2">
            {tools.map((tool) => (
              <div
                key={tool.id}
                className="flex flex-col items-center cursor-pointer w-[114px] h-[114px] relative rounded border border-[#edebe6]"
                onClick={() => handleIconClick(tool.id)}
                title={
                  activeTab === "ELEVATION"
                    ? t('structures.elevationUnavailableIn3D')
                    : undefined
                }
              >
                {tool.preview}
                <div className="left-[6px] top-[94px] absolute text-black text-xs font-normal font-['DM Sans']">
                  {t(tool.labelKey)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

    </div>

  );
});
